import { Briefcase, CheckSquare, Layout, MessageSquare, Star } from 'react-feather';

import { NavBarItem } from '@/components/Header';
import config from '@/config';

// --- ITEMS TEMPLATE

const itemDashboard = {
  key: 'dashboard',
  text: 'Dashboard',
  to: '/dashboard',
  icon: Layout,
};

const itemMessaging = {
  key: 'messaging',
  text: 'Messages',
  to: '/messaging',
  icon: MessageSquare,
};

const itemCalls = {
  key: 'consultations',
  text: 'Consultations',
  to: '/consultations',
  icon: Briefcase,
};

const itemEngagements = {
  key: 'engagements',
  text: 'Engagements',
  to: '/engagements',
  icon: Briefcase,
};

const itemApprovals = {
  key: 'engagement-approvals',
  text: 'Approvals',
  to: '/approvals',
  icon: CheckSquare,
};

const itemExpertRequest = {
  key: 'expert_requests',
  text: 'Expert Requests',
  to: '/expert_requests',
  icon: Star,
  id: 'expertRequestsAdmin',
};

const itemMyClients = {
  key: 'my-network',
  text: 'My Clients',
  to: '/network',
  icon: Star,
};

const itemMyNetwork = {
  key: 'my-network',
  text: 'My Network',
  to: '/search',
  icon: Star,
};

const itemBecomeAnExpert = {
  key: 'signup/expert',
  text: 'Become an Expert',
  to: '/signup/expert',
  promo: true,
};

// --- BUILDER

interface NavItemsBuilderOptions {
  unreadMessages: number | null;
  userContext: string;
  isAdmin: boolean;
  isAccountEnterprise: unknown;
  hasExpertState?: boolean;
  /** `canViewEngagementSurfaces(viewer, userContext)` — the ENG-5607 allowlist gate. */
  engagementsEnabled?: boolean;
  /**
   * `useIsGovernanceApprover(...)` — whether the viewer may take governance
   * decisions anywhere (ENG-5654). Gates Approvals ALONE; Engagements is
   * unaffected.
   *
   * REQUIRED, unlike its neighbours, because the failure is silent in the wrong
   * direction: optional plus a falsy default means a caller that forgets it
   * withholds Approvals from a real approver, with nothing to notice. Required
   * turns that into a compile error at the call site.
   */
  isGovernanceApprover: boolean;
}

export const navItemsBuilder = ({
  unreadMessages,
  userContext,
  isAdmin,
  isAccountEnterprise,
  hasExpertState,
  engagementsEnabled,
  isGovernanceApprover,
}: NavItemsBuilderOptions): NavBarItem[] => {
  const newItemMessaging = {
    ...itemMessaging,
    text: <span>Messages {unreadMessages}</span>,
  };

  const items: NavBarItem[] = [itemDashboard];

  if (config.enableChat) {
    items.push(newItemMessaging);
  }

  items.push(itemCalls);

  // Consulting engagements (ENG-5399).
  //
  // Both sides get the list. `myEngagementBuckets` became viewer-aware in
  // ENG-5609, so a customer entry now opens a page it can fill — the
  // expert-only split here was a workaround for the api having no customer
  // list, and it sent every customer to Approvals, which is empty for the
  // non-approvers who are most of them.
  //
  // Approvals stays a separate entry rather than a bucket: it is a different
  // job (deciding on someone else's engagement) and it is reachable from the
  // governance rows in the list.
  //
  // It also follows the QUEUE, not just the feature flag (ENG-5654). The queue
  // is approver-only, and governance resolves to the team's active owner — a
  // set of one, by the single-owner invariant — so gating this on `not an
  // expert` handed everyone else a permanently empty inbox. Internal staff
  // most of all, since the engagements gate short-circuits on `viewer.admin`.
  //
  // Eligibility, not queue contents: an approver keeps the entry once caught
  // up. The expert check stays because it is cheaper than the query and the
  // answer is known — an expert holds no team role.
  if (engagementsEnabled) {
    items.push(itemEngagements);
    if (userContext !== 'expert' && isGovernanceApprover) {
      items.push(itemApprovals);
    }
  }

  if (userContext !== 'expert') {
    items.push(itemExpertRequest);
  }

  if (userContext === 'expert') {
    items.push(itemMyClients);
  } else if (!isAdmin && isAccountEnterprise) {
    items.push(itemMyNetwork);
  }

  if (!hasExpertState) {
    items.push(itemBecomeAnExpert);
  }

  return items;
};
